/**
 * Read-only Shopify Storefront access for Her Collection.
 * The storefront token is a public, read/cart-only key (safe in client code).
 * Checkout is intentionally NOT implemented yet.
 */
export const SHOPIFY_API_VERSION = "2026-07";
export const SHOPIFY_STORE_PERMANENT_DOMAIN = "f181n6-4d.myshopify.com";
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
export const SHOPIFY_STOREFRONT_TOKEN = "998cd9515b2237746d87cb18c4a6988f";

export type Money = { amount: string; currencyCode: string };
export type CollectionVariant = {
  id: string;
  title: string;
  price: Money;
  availableForSale: boolean;
  selectedOptions: { name: string; value: string }[];
};
export type CollectionProduct = {
  id: string;
  title: string;
  handle: string;
  description: string;
  athleteId: string | null;
  image: { url: string; altText: string | null } | null;
  priceRange: { minVariantPrice: Money };
  options: { name: string; values: string[] }[];
  variants: CollectionVariant[];
};

export async function storefrontApiRequest<T = any>(query: string, variables: Record<string, unknown> = {}): Promise<T> {
  const res = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN },
    body: JSON.stringify({ query, variables }),
  });
  if (res.status === 402) throw new Error("Shopify store needs an active plan to serve products.");
  if (!res.ok) throw new Error(`Shopify request failed (${res.status})`);
  const json = await res.json();
  if (json.errors?.length) throw new Error(json.errors.map((e: { message: string }) => e.message).join(", "));
  return json as T;
}

const PRODUCTS_QUERY = `
  query HerCollection($first: Int!, $after: String) {
    products(first: $first, after: $after) {
      pageInfo { hasNextPage endCursor }
      edges { node {
        id title handle description
        athlete: metafield(namespace: "siis", key: "athlete_id") { value }
        priceRange { minVariantPrice { amount currencyCode } }
        featuredImage { url altText }
        options { name values }
        variants(first: 50) { edges { node {
          id title availableForSale price { amount currencyCode } selectedOptions { name value }
        } } }
      } }
    }
  }`;

export const normalizeProduct = (n: any): CollectionProduct => ({
  id: n.id,
  title: n.title,
  handle: n.handle,
  description: n.description ?? "",
  athleteId: n.athlete?.value?.trim() || null,
  image: n.featuredImage ?? null,
  priceRange: n.priceRange,
  options: (n.options ?? []).filter((o: { name: string; values: string[] }) => !(o.name === "Title" && o.values.length === 1)),
  variants: (n.variants?.edges ?? []).map((e: { node: CollectionVariant }) => e.node),
});

/** Products whose siis.athlete_id exactly equals the permanent SIIS athlete id. Never guesses. */
export const filterByAthlete = (products: CollectionProduct[], athleteId: string) =>
  products.filter((p) => p.athleteId !== null && p.athleteId === athleteId);

/** "gid://shopify/Product/123" or "123" → "123". */
export const numericShopifyId = (id: string) => id.split("/").pop() ?? id;

/** Both checks must pass: Shopify metafield match AND an approved SIIS product record for that athlete. */
export const filterByApprovedMapping = (products: CollectionProduct[], athleteId: string, approvedIds: Set<string>) =>
  filterByAthlete(products, athleteId).filter((p) => approvedIds.has(numericShopifyId(p.id)));

export async function fetchAthleteCollection(athleteId: string, approvedIds: Set<string>): Promise<CollectionProduct[]> {
  const all: CollectionProduct[] = [];
  let after: string | null = null;
  for (let page = 0; page < 5; page++) {
    const data: any = await storefrontApiRequest(PRODUCTS_QUERY, { first: 100, after });
    const conn = data?.data?.products;
    all.push(...(conn?.edges ?? []).map((e: { node: unknown }) => normalizeProduct(e.node)));
    if (!conn?.pageInfo?.hasNextPage) break;
    after = conn.pageInfo.endCursor;
  }
  return filterByApprovedMapping(all, athleteId, approvedIds);
}

export const formatMoney = (m: Money) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: m.currencyCode }).format(Number(m.amount));
